import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo
} from 'react';
import { useQueue } from '@strava/react-hooks';
import { generateRandomId } from '@strava/utils';
import { AlertStatusType } from '../Alert';

export interface AlertConfig {
  alertType: AlertStatusType;
  content: ReactNode;
  containerId?: string;
  id?: string;
  className?: string;
  autoDismiss?: boolean;
  autoDismissDelay?: number;
  contentPosition?: 'left' | 'center' | 'right';
  hideCloseButton?: boolean;
  hideIcon?: boolean;
  iconSize?: number;
  onClose?: () => void;
}

export interface AlertState {
  alerts: AlertConfig[];
  showAlert: (config: AlertConfig) => string;
  hideAlert: (id: string) => void;
  clearAlerts: (containerId?: string) => void;
}

const AlertContext = createContext<AlertState | undefined>(undefined);
AlertContext.displayName = 'AlertContext';

export const useAlertContext = () => {
  const context = useContext(AlertContext);

  // Fail fast if useAlertContext hook is used outside the Provider.
  if (!context) {
    throw new Error(`useAlertContext must be used within the AlertProvider`);
  }

  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider = ({ children }: AlertProviderProps) => {
  const { queue, updateQueue, clearQueue } = useQueue<AlertConfig>({
    initialValues: []
  });

  /**
   * @description Display an alert.
   *
   * @param {Object} alertConfig
   * @param {'error' | 'warning' | 'info' | 'success'} alertConfig.alertType
   * @param {ReactNode} alertConfig.content - Message to display in the the content of the Alert Component.
   * @param {string} [alertConfig.containerId="base"] - It's used to identify which `AlertContainer` the alert is associated to.
   * @param {string | number} [alertConfig.id] - Optional; by default id is randomly generated. Used to update or close alert.
   * @param {string} [alertConfig.className] - The className passed to Alert component.
   * @param {boolean} [alertConfig.autoDismiss=false] - Determine if alert will auto dismiss/close.
   * @param {number} [alertConfig.autoDismissDelay=8000] - Delay in ms that alert will auto dismiss.
   * @param {'left' | 'center' | 'right'} [alertConfig.contentPosition="left"] - Position to align content inside of alert container.
   * @param {boolean} [alertConfig.hideCloseButton=false] - Hides the close button, alert can only be closed programmatically.
   * @param {boolean} [alertConfig.hideIcon=false] - Display or hide alert icon.
   * @param {number} [alertConfig.iconSize=32] - The Alert's SVG icon's width and height.
   * @param {Function} [alertConfig.onClose] - Callback fn; called when alert closes.
   *
   * @return {string} - Alert ID
   */
  const showAlert = useCallback(
    (alertConfig: AlertConfig) => {
      const id = alertConfig.id ?? generateRandomId();
      const containerId = alertConfig.containerId ?? 'base';

      updateQueue((alerts) => {
        const hasAlert =
          alertConfig?.id &&
          alerts.some((alert) => alert.id === alertConfig.id);
        // Check if alert exist in queue.
        // If true then return hasAlerts
        // Else add/append alert to queue.
        if (hasAlert) {
          return alerts;
        }

        return [...alerts, { ...alertConfig, id, containerId }];
      });

      return `${id}`;
    },
    [updateQueue]
  );

  /**
   * @description Hide an alert by id.
   *
   * @param {string} id - Unique id to hide/remove alert.
   *
   * @return {boolean}
   * */
  const hideAlert = useCallback(
    (id: string) =>
      updateQueue((alerts) =>
        alerts.filter((alert) => {
          if (alert.id === id) {
            // eslint-disable-next-line no-unused-expressions
            alert?.onClose?.();
            return false;
          }

          return true;
        })
      ),
    [updateQueue]
  );

  /**
   * @description Clears/remove all alerts.
   * If no `containerId` provided then removes ALL alerts. Else if `containerId`
   * provided then clears alerts for specific container when working with multiple containers.
   *
   * @param {string} [containerId] - Optional; Clear alerts for specific container when working with multiple containers.
   *
   * @return {boolean}
   * */
  const clearAlerts = useCallback(
    (containerId: string | undefined) => {
      // If containerId is passed then remove all alerts for specific container
      if (containerId) {
        return updateQueue((alerts) =>
          alerts.filter((alert) => {
            if (alert.containerId === containerId) {
              // eslint-disable-next-line no-unused-expressions
              alert?.onClose?.();
              return false;
            }

            return true;
          })
        );
      }
      // Else clear entire queue
      return clearQueue();
    },
    [updateQueue, clearQueue]
  );

  const providerState = useMemo(
    () => ({
      alerts: queue,
      showAlert,
      hideAlert,
      clearAlerts
    }),
    [queue, showAlert, hideAlert, clearAlerts]
  );

  return (
    <AlertContext.Provider value={providerState}>
      {children}
    </AlertContext.Provider>
  );
};
